import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {Channels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {MergeMethod} from '@github-ui/mergebox/types'
import type {MergeStatusButtonData} from '../page-data/payloads/header'
import {isPullRequestOpenState} from '../utils/pull-request-state'
import type {PullRequestState} from '../types'

export type ValidatedMergeStatusButtonData = {
  channels: Channels
  defaultMergeMethod: MergeMethod
  headSha: string
  helpUrl: string
  viewerLogin: string
}

/**
 * Determines whether the merge status button should be rendered.
 * Returns the validated data needed to render the button, or null if it should not be rendered.
 *
 * Uses `useFeatureFlag` for the opt-out check to support org-level flag evaluation.
 */
export function useMergeStatusButtonData(
  pullRequestState: PullRequestState,
  mergeStatusButtonData?: MergeStatusButtonData,
): ValidatedMergeStatusButtonData | null {
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isOptedOut = useFeatureFlag('pull_request_merge_status_button_opt_out')

  const isPROpen = isPullRequestOpenState(pullRequestState)

  if (!isPROpen || isOptedOut) return null

  const {channels, headSha, helpUrl, viewerLogin, defaultMergeMethod} = mergeStatusButtonData || {}

  if (!channels || !headSha || !helpUrl || !viewerLogin || !defaultMergeMethod) return null

  return {channels, defaultMergeMethod, headSha, helpUrl, viewerLogin}
}
